/** @jsx jsx */

import { useMemo, useCallback } from 'react';
import { css, jsx } from '@emotion/react';
import { AnnotationSharedCSSByState } from '@atlaskit/editor-common/styles';
import { AnnotationMarkStates } from '@atlaskit/adf-schema';
import { getBooleanFF } from '@atlaskit/platform-feature-flags';

// eslint-disable-next-line @atlaskit/design-system/no-css-tagged-template-expression -- `AnnotationSharedCSSByState` is not object-safe
const markStyles = () => css`
  color: inherit;
  background-color: unset;
  -webkit-tap-highlight-color: transparent;

  &[data-mark-annotation-state='${AnnotationMarkStates.ACTIVE}'] {
    ${AnnotationSharedCSSByState().blur};

    &:focus,
    &[data-has-focus='true'] {
      ${AnnotationSharedCSSByState().focus};
    }
  }
`;
export const MarkComponent = ({
  annotationParentIds,
  children,
  dataAttributes,
  id,
  state,
  hasFocus,
  onClick,
  useBlockLevel
}) => {
  const annotationIds = useMemo(() => [...new Set([...annotationParentIds, id])], [id, annotationParentIds]);
  const onMarkClick = useCallback(event => {
    // prevents multiple callback on overlapping annotations
    if (event.defaultPrevented || state !== AnnotationMarkStates.ACTIVE) {
      return;
    }

    // prevents from opening link URL inside webView in Safari
    event.preventDefault();
    if (getBooleanFF('platform.editor.allow-inline-comments-for-inline-nodes')) {
      event.stopPropagation();
    }
    onClick({
      eventTarget: event.target,
      annotationIds
    });
  }, [annotationIds, onClick, state]);
  const overriddenData = !state ? dataAttributes : {
    ...dataAttributes,
    'data-mark-annotation-state': state,
    'data-has-focus': hasFocus
  };
  const accessibility = state !== AnnotationMarkStates.ACTIVE ? {
    'aria-disabled': true
  } : {
    'aria-details': annotationIds.join(', ')
  };
  return jsx(useBlockLevel ? 'div' : 'mark', {
    id,
    [getBooleanFF('platform.editor.allow-inline-comments-for-inline-nodes') ? 'onClickCapture' : 'onClick']: onMarkClick,
    ...accessibility,
    ...overriddenData,
    ...(!useBlockLevel && {
      css: markStyles
    })
  }, children);
};